import { useCallback, useMemo, useState } from 'react';
import { useBootstrapper } from '@/ui-kit/contexts/BootstrapperContext';
import { useLocalization } from '@canvas-logic/clp-shared-ui';

type State = 'idle' | 'ready' | 'loading';

type Value = {
  state: State;
  generate: () => Promise<void>;
};

export const useGetPdf = () => {
  const { bootstrapper } = useBootstrapper();
  // Active-language translation layers (ADR-0075). Passed into the PDF
  // service so the rendered document uses CSV-imported translations, falling
  // back to i18next / SDK texts when a translation is missing.
  const { layers } = useLocalization();
  const [state, setState] = useState<State>('idle');

  const downloadPdf = useCallback(async () => {
    const link = await bootstrapper?.createLink();
    if (!link) {
      return;
    }

    await bootstrapper?.downloadPdf(link, layers);
  }, [bootstrapper, layers]);

  const generate = useCallback(async () => {
    if (!bootstrapper) {
      return;
    }

    setState('loading');
    try {
      await downloadPdf();
      setState('ready');

      setTimeout(() => {
        setState('idle');
      }, 2000);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      setState('idle');
    }
  }, [bootstrapper, downloadPdf]);

  return useMemo<Value>(
    () => ({
      state,
      generate,
    }),
    [state, generate],
  );
};
